import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const ListenPart1 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal, zoomLevel = 1 }) => {
  const [lines, setLines] = useState([]);
  const [loadedLines, setLoadedLines] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [previewLine, setPreviewLine] = useState(null);
  const [imageOffset, setImageOffset] = useState({ top: 0, left: 0 });
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [hintLines, setHintLines] = useState([]);
  const [exampleLine, setExampleLine] = useState(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [layoutVersion, setLayoutVersion] = useState(0);

  const mainContentRef = useRef(null);
  const imageContainerRef = useRef(null);
  const nameRefs = useRef({});
  const [refsReady, setRefsReady] = useState(false);

  // Reset state when exercise changes
  useEffect(() => {
    // Don't clear nameRefs here — they are attached during render and
    // clearing them after render causes refsReady to never become true.
    setRefsReady(false);
    setIsImageLoaded(false);
    setLines([]);
    setHintLines([]);
    setExampleLine(null);
    setIsHydrated(false);
    setLoadedLines(null);
  }, [exerciseId]);

  // Helper variables (safe to access conditionally or default)
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  // Safe accessors
  const getZones = () => exerciseData?.content_data?.zones || [];
  const getQuestions = () => exerciseData?.questions || [];
  const wordBankSafe = useMemo(() => exerciseData?.questions?.map(q => q.question_text) || [], [exerciseData]);

  // ResizeObserver to detect any layout shifts (fonts loading, image height expanding, window resize)
  useLayoutEffect(() => {
    if (!mainContentRef.current) return;
    const observer = new ResizeObserver(() => {
      setLayoutVersion(v => v + 1);
    });
    observer.observe(mainContentRef.current);
    return () => observer.disconnect();
  }, []);

  // Detect when all name refs are attached (after each render)
  useLayoutEffect(() => {
    if (wordBankSafe.length === 0) return;
    const allMounted = wordBankSafe.every(name => !!nameRefs.current[name]);
    console.log('[ListenPart1] refsReady check', { wordBankSafe, allMounted, nameRefKeys: Object.keys(nameRefs.current) });
    if (allMounted) {
      if (!refsReady) setRefsReady(true);
    } else {
      if (refsReady) setRefsReady(false);
    }
  }, [wordBankSafe, refsReady, layoutVersion]);

  const updateScale = useCallback(() => {
    const imageElement = imageContainerRef.current?.querySelector('img');
    if (imageElement) {
      const rect = imageElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && imageElement.naturalWidth > 0) {
        setScale({
          x: imageElement.naturalWidth / (rect.width / zoomLevel),
          y: imageElement.naturalHeight / (rect.height / zoomLevel)
        });
      }
    }
  }, [zoomLevel]);

  const handleImageLoad = () => {
    setIsImageLoaded(true);
    updateScale();
    setLayoutVersion(v => v + 1);
  };

  useEffect(() => {
    if (isImageLoaded) {
      updateScale();
    }
  }, [isImageLoaded, layoutVersion, updateScale]);

  // Handle cached images: if the image is already in the browser cache,
  // the onLoad event won't fire after a state reset, so we check manually.
  useEffect(() => {
    if (isImageLoaded) return; // Already loaded, nothing to do
    const timer = setTimeout(() => {
      if (imageContainerRef.current) {
        const imgEl = imageContainerRef.current.querySelector('img');
        if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
          setIsImageLoaded(true);
          updateScale();
          setLayoutVersion(v => v + 1);
        }
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [exerciseId, isImageLoaded, updateScale]);

  const getObjectFitSize = useCallback((containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight) => {
    if (!containerWidth || !containerHeight || !imageNaturalWidth || !imageNaturalHeight) return null;

    const containerRatio = containerWidth / containerHeight;
    const imageRatio = imageNaturalWidth / imageNaturalHeight;

    let targetWidth = containerWidth;
    let targetHeight = containerHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (imageRatio > containerRatio) {
      // Image is wider than container, height is scaled down and letterboxed
      targetHeight = containerWidth / imageRatio;
      offsetY = (containerHeight - targetHeight) / 2;
    } else {
      // Image is taller than container, width is scaled down and pillarboxed
      targetWidth = containerHeight * imageRatio;
      offsetX = (containerWidth - targetWidth) / 2;
    }

    return {
      width: targetWidth,
      height: targetHeight,
      offsetX,
      offsetY,
      scaleX: imageNaturalWidth / targetWidth,
      scaleY: imageNaturalHeight / targetHeight,
    };
  }, []);

  const findZoneForPoint = (x, y, zones) => {
    for (const zone of zones) {
      if (x >= zone.rect.x1 && x <= zone.rect.x2 && y >= zone.rect.y1 && y <= zone.rect.y2) {
        return zone;
      }
    }
    return null;
  };

  const handleNameClick = (name) => {
    if (isReviewMode || !isHydrated || !refsReady) return;
    const word_bank = wordBankSafe;
    const middleIndex = Math.ceil(word_bank.length / 2);
    const bottomNames = word_bank.slice(middleIndex);

    setSelectedName(name);
    const mainContentRect = mainContentRef.current.getBoundingClientRect();
    const nameRef = nameRefs.current[name];
    if (!nameRef) return;
    const nameRect = nameRef.getBoundingClientRect();

    const fromX = (nameRect.left + nameRect.width / 2 - mainContentRect.left) / zoomLevel;
    let fromY;
    if (bottomNames.includes(name)) {
      fromY = (nameRect.top - mainContentRect.top - 5) / zoomLevel;
    } else {
      fromY = (nameRect.bottom - mainContentRect.top + 5) / zoomLevel;
    }
    setPreviewLine({ fromPos: { x: fromX, y: fromY }, toPos: { x: fromX, y: fromY } });
  };

  const handleImageClick = (event) => {
    if (isReviewMode || !selectedName || !previewLine || !isHydrated || !isImageLoaded) return;

    const mainContentElement = mainContentRef.current;
    const imageElement = imageContainerRef.current.querySelector('img');
    if (!mainContentElement || !imageElement) return;

    const mainRect = mainContentElement.getBoundingClientRect();
    const imageRect = imageElement.getBoundingClientRect();

    const clickX_Container = (event.clientX - mainRect.left) / zoomLevel;
    const clickY_Container = (event.clientY - mainRect.top) / zoomLevel;

    const clickX_Image = (event.clientX - imageRect.left) / zoomLevel;
    const clickY_Image = (event.clientY - imageRect.top) / zoomLevel;

    const layoutImageWidth = imageRect.width / zoomLevel;
    const layoutImageHeight = imageRect.height / zoomLevel;

    if (layoutImageWidth <= 0 || layoutImageHeight <= 0) return;

    const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, imageElement.naturalWidth, imageElement.naturalHeight);
    if (!fitParams) return;

    // Check if the click is within the actual letterboxed/pillarboxed image area
    if (
      clickX_Image < fitParams.offsetX || clickX_Image > fitParams.offsetX + fitParams.width ||
      clickY_Image < fitParams.offsetY || clickY_Image > fitParams.offsetY + fitParams.height
    ) {
      return; // Clicked in the empty border region created by object-contain
    }

    const originalX = (clickX_Image - fitParams.offsetX) * fitParams.scaleX;
    const originalY = (clickY_Image - fitParams.offsetY) * fitParams.scaleY;

    const targetZone = findZoneForPoint(originalX, originalY, getZones());

    const finalToPos = { x: clickX_Container, y: clickY_Container };

    setLines(prevLines => {
      const otherLines = prevLines.filter(line => line.from !== selectedName);
      return [...otherLines, {
        from: selectedName,
        fromPos: previewLine.fromPos,
        toPos: finalToPos,
        toZone: targetZone
      }];
    });

    setSelectedName(null);
    setPreviewLine(null);
  };

  const handleClearLines = () => {
    if (isReviewMode || !isHydrated) return;
    setLines([]);
  };

  // --- HOOKS ---

  useEffect(() => {
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [updateScale]);

  // Initial Calculation
  useEffect(() => {
    console.log('[ListenPart1] exampleLine effect', { isImageLoaded, refsReady, hasExerciseData: !!exerciseData, hasContentData: !!exerciseData?.content_data, mainContentRef: !!mainContentRef.current, imageContainerRef: !!imageContainerRef.current });
    if (isImageLoaded && refsReady && exerciseData && exerciseData.content_data && mainContentRef.current && imageContainerRef.current) {
      const { zones } = exerciseData.content_data;
      const questions = exerciseData.questions;
      if (!questions) return;
      const exampleQuestion = questions.find(q => q.order_index === 0);

      if (exampleQuestion) {
        const word_bank = questions.map(q => q.question_text);
        const middleIndex = Math.ceil(word_bank.length / 2);
        const bottomNames = word_bank.slice(middleIndex);

        const mainContentRect = mainContentRef.current.getBoundingClientRect();
        const imgEl = imageContainerRef.current.querySelector('img');
        if (!imgEl) return;
        const imageRect = imgEl.getBoundingClientRect();
        const imageOffset = {
          top: (imageRect.top - mainContentRect.top) / zoomLevel,
          left: (imageRect.left - mainContentRect.left) / zoomLevel,
        };

        const fromEl = nameRefs.current[exampleQuestion.question_text];
        if (fromEl) {
          const nameRect = fromEl.getBoundingClientRect();
          const fromX = (nameRect.left + nameRect.width / 2 - mainContentRect.left) / zoomLevel;
          let fromY;
          if (bottomNames.includes(exampleQuestion.question_text)) {
            fromY = (nameRect.top - mainContentRect.top - 5) / zoomLevel;
          } else {
            fromY = (nameRect.bottom - mainContentRect.top + 5) / zoomLevel;
          }
          const fromPos = { x: fromX, y: fromY };

          if (exampleQuestion.accepted_answers && exampleQuestion.accepted_answers.length > 0) {
            const correctZoneName = exampleQuestion.accepted_answers[0];
            const zone = zones.find(z => z.name === correctZoneName);
            if (zone) {
              const layoutImageWidth = imageRect.width / zoomLevel;
              const layoutImageHeight = imageRect.height / zoomLevel;
              if (layoutImageWidth <= 0 || layoutImageHeight <= 0 || imgEl.naturalWidth <= 0) return;

              const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, imgEl.naturalWidth, imgEl.naturalHeight);
              if (!fitParams) return;

              const zoneCenterX = (zone.rect.x1 + zone.rect.x2) / 2;
              const zoneCenterY = (zone.rect.y1 + zone.rect.y2) / 2;

              const layoutZoneX = zoneCenterX / fitParams.scaleX + fitParams.offsetX;
              const layoutZoneY = zoneCenterY / fitParams.scaleY + fitParams.offsetY;

              const toPos = { x: layoutZoneX + imageOffset.left, y: layoutZoneY + imageOffset.top };

              setExampleLine({ fromPos, toPos, from: exampleQuestion.question_text, toZone: zone });
            }
          }
        }
      }
    }
  }, [isImageLoaded, exerciseData, isHydrated, zoomLevel, refsReady, layoutVersion]);

  useEffect(() => {
    if (mainContentRef.current && imageContainerRef.current && isImageLoaded) {
      const mainRect = mainContentRef.current.getBoundingClientRect();
      const imgEl = imageContainerRef.current.querySelector('img') || imageContainerRef.current;
      const imageRect = imgEl.getBoundingClientRect();
      setImageOffset({
        top: (imageRect.top - mainRect.top) / zoomLevel,
        left: (imageRect.left - mainRect.left) / zoomLevel,
      });
    }
  }, [exerciseData, isImageLoaded, zoomLevel, layoutVersion]);

  // Hydration
  useEffect(() => {
    if (attemptId && exerciseId && exerciseData && exerciseData.content_data) {
      const { zones } = exerciseData.content_data;
      if (isReviewMode && exerciseData.questions) {
        const apiLines = exerciseData.questions.map(q => {
          if (!q.user_answer) return null;
          const answerZoneName = q.user_answer.answer;
          const zone = zones.find(z => z.name === answerZoneName);

          let parsedToPos = { x: 0, y: 0 };
          if (!zone) {
            try {
              // The backend answer for non-zoned points might be a JSON string of coordinates.
              const parsed = JSON.parse(answerZoneName);
              if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
                parsedToPos = parsed;
              }
            } catch (e) {
              parsedToPos = { x: 0, y: 0 };
            }
          }

          return {
            from: q.question_text,
            toZone: zone,
            fromPos: { x: 0, y: 0 },
            toPos: parsedToPos
          };
        }).filter(Boolean);

        if (apiLines.length > 0) {
          setLoadedLines(apiLines);
          return;
        } else {
          setIsHydrated(true);
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];

          if (savedData) {
            let linesToLoad = [];
            if (Array.isArray(savedData)) {
              linesToLoad = savedData;
            } else if (typeof savedData === 'object' && savedData !== null) {
              linesToLoad = Object.values(savedData).map(item => {
                return { ...item.line_data, from: item.question_text };
              });
            }

            if (linesToLoad.length > 0) {
              setLoadedLines(linesToLoad);
            } else {
              setLines([]);
              setIsHydrated(true);
            }
          } else {
            setIsHydrated(true);
          }
        } catch (e) {
          localStorage.removeItem(attemptKey);
          setIsHydrated(true);
        }
      } else {
        setIsHydrated(true);
      }
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData]);

  // Recalculate Loaded Lines
  useEffect(() => {
    if (loadedLines && mainContentRef.current && imageContainerRef.current && isImageLoaded && refsReady && exerciseData && exerciseData.content_data) {
      const { zones } = exerciseData.content_data;
      const questions = exerciseData.questions;
      const word_bank = questions.map(q => q.question_text);
      const middleIndex = Math.ceil(word_bank.length / 2);
      const bottomNames = word_bank.slice(middleIndex);

      const mainContentRect = mainContentRef.current.getBoundingClientRect();
      const imgEl = imageContainerRef.current.querySelector('img');
      if (!imgEl) return;
      const imageRect = imgEl.getBoundingClientRect();
      const imageOffset = {
        top: (imageRect.top - mainContentRect.top) / zoomLevel,
        left: (imageRect.left - mainContentRect.left) / zoomLevel,
      };

      const layoutImageWidth = imageRect.width / zoomLevel;
      const layoutImageHeight = imageRect.height / zoomLevel;
      if (layoutImageWidth <= 0 || layoutImageHeight <= 0 || imgEl.naturalWidth <= 0) return;

      const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, imgEl.naturalWidth, imgEl.naturalHeight);
      if (!fitParams) return;

      const recalculatedLines = loadedLines.map(line => {
        const fromEl = nameRefs.current[line.from];
        if (!fromEl) return null;

        const nameRect = fromEl.getBoundingClientRect();
        const fromX = (nameRect.left + nameRect.width / 2 - mainContentRect.left) / zoomLevel;
        let fromY;
        if (bottomNames.includes(line.from)) {
          fromY = (nameRect.top - mainContentRect.top - 5) / zoomLevel;
        } else {
          fromY = (nameRect.bottom - mainContentRect.top + 5) / zoomLevel;
        }
        const fromPos = { x: fromX, y: fromY };

        let toPos = line.toPos;
        if (line.toZone) {
          const zone = zones.find(z => z.name === line.toZone.name);
          if (zone) {
            const zoneCenterX = (zone.rect.x1 + zone.rect.x2) / 2;
            const zoneCenterY = (zone.rect.y1 + zone.rect.y2) / 2;

            const layoutZoneX = zoneCenterX / fitParams.scaleX + fitParams.offsetX;
            const layoutZoneY = zoneCenterY / fitParams.scaleY + fitParams.offsetY;

            toPos = { x: layoutZoneX + imageOffset.left, y: layoutZoneY + imageOffset.top };
          }
        }

        return { ...line, fromPos, toPos };
      }).filter(Boolean);

      setLines(recalculatedLines);
      setLoadedLines(null);
      setIsHydrated(true);
    }
  }, [loadedLines, isImageLoaded, refsReady, exerciseData, zoomLevel, layoutVersion]);

  // Save Answers
  const saveAnswersToLocalStorage = useCallback((currentLines) => {
    if (!isHydrated) return;

    if (attemptId && exerciseId && exerciseData && exerciseData.questions) {
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      let allAnswers = {};
      if (savedAttempt) {
        try {
          allAnswers = JSON.parse(savedAttempt);
        } catch (e) {
          allAnswers = {};
        }
      }

      if (currentLines.length === 0 && !allAnswers[exerciseId]) return;

      const answersToSave = {};
      currentLines.forEach(line => {
        const question = exerciseData.questions.find(q => q.question_text === line.from);
        if (question) {
          const { from, ...lineDataForSaving } = line;
          answersToSave[question.id] = {
            question_text: line.from,
            answer: line.toZone ? line.toZone.name : null,
            line_data: lineDataForSaving,
          };
        }
      });

      allAnswers[exerciseId] = answersToSave;
      localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
    }
  }, [attemptId, exerciseId, isHydrated, exerciseData]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(lines);
    }, 500);
    return () => clearTimeout(debounceSave);
  }, [lines, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    console.log('[ListenPart1] fillHint effect', { fillHintSignal, isImageLoaded, refsReady, hasExerciseData: !!exerciseData, mainContentRef: !!mainContentRef.current, imageContainerRef: !!imageContainerRef.current });
    if (fillHintSignal > 0 && isImageLoaded && refsReady && exerciseData && exerciseData.content_data && exerciseData.questions) {
      const { zones } = exerciseData.content_data;
      const questions = exerciseData.questions.filter(q => q.order_index !== 0); // Exclude example
      const word_bank = exerciseData.questions.map(q => q.question_text);
      const middleIndex = Math.ceil(word_bank.length / 2);
      const bottomNames = word_bank.slice(middleIndex);

      const mainContentRect = mainContentRef.current.getBoundingClientRect();
      const imgEl = imageContainerRef.current.querySelector('img');
      if (!imgEl) return;
      const imageRect = imgEl.getBoundingClientRect();
      const imageOffset = {
        top: (imageRect.top - mainContentRect.top) / zoomLevel,
        left: (imageRect.left - mainContentRect.left) / zoomLevel,
      };

      const layoutImageWidth = imageRect.width / zoomLevel;
      const layoutImageHeight = imageRect.height / zoomLevel;
      if (layoutImageWidth <= 0 || layoutImageHeight <= 0 || imgEl.naturalWidth <= 0) return;

      const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, imgEl.naturalWidth, imgEl.naturalHeight);
      if (!fitParams) return;

      const newLines = questions.map(q => {
        const fromEl = nameRefs.current[q.question_text];
        if (!fromEl) return null;

        const nameRect = fromEl.getBoundingClientRect();
        const fromX = (nameRect.left + nameRect.width / 2 - mainContentRect.left) / zoomLevel;
        let fromY;
        if (bottomNames.includes(q.question_text)) {
          fromY = (nameRect.top - mainContentRect.top - 5) / zoomLevel;
        } else {
          fromY = (nameRect.bottom - mainContentRect.top + 5) / zoomLevel;
        }
        const fromPos = { x: fromX, y: fromY };

        if (!q.accepted_answers || q.accepted_answers.length === 0) return null;
        const correctZoneName = q.accepted_answers[0];
        const zone = zones.find(z => z.name === correctZoneName);
        if (!zone) return null;

        const zoneCenterX = (zone.rect.x1 + zone.rect.x2) / 2;
        const zoneCenterY = (zone.rect.y1 + zone.rect.y2) / 2;

        const layoutZoneX = zoneCenterX / fitParams.scaleX + fitParams.offsetX;
        const layoutZoneY = zoneCenterY / fitParams.scaleY + fitParams.offsetY;

        const toPos = { x: layoutZoneX + imageOffset.left, y: layoutZoneY + imageOffset.top };

        return { fromPos, toPos, from: q.question_text, toZone: zone };
      }).filter(Boolean);

      setLines(newLines);
      saveAnswersToLocalStorage(newLines);
    }
  }, [fillHintSignal, isImageLoaded, refsReady, exerciseData, zoomLevel, saveAnswersToLocalStorage, layoutVersion]);

  // Hint — also draw correct-answer lines in review mode
  useEffect(() => {
    console.log('[ListenPart1] hint effect', { showHint, isReviewMode, isImageLoaded, refsReady, hasExerciseData: !!exerciseData, mainContentRef: !!mainContentRef.current });
    if ((showHint || isReviewMode) && isImageLoaded && refsReady && exerciseData && exerciseData.content_data && exerciseData.questions) {
      const { zones } = exerciseData.content_data;
      const questions = exerciseData.questions;
      const word_bank = questions.map(q => q.question_text);
      const middleIndex = Math.ceil(word_bank.length / 2);
      const bottomNames = word_bank.slice(middleIndex);

      const mainContentRect = mainContentRef.current.getBoundingClientRect();
      const imgEl = imageContainerRef.current.querySelector('img');
      if (!imgEl) return;
      const imageRect = imgEl.getBoundingClientRect();
      const imageOffset = {
        top: (imageRect.top - mainContentRect.top) / zoomLevel,
        left: (imageRect.left - mainContentRect.left) / zoomLevel,
      };

      const layoutImageWidth = imageRect.width / zoomLevel;
      const layoutImageHeight = imageRect.height / zoomLevel;
      if (layoutImageWidth <= 0 || layoutImageHeight <= 0 || imgEl.naturalWidth <= 0) return;

      const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, imgEl.naturalWidth, imgEl.naturalHeight);
      if (!fitParams) return;

      const newHintLines = questions.map(q => {
        const fromEl = nameRefs.current[q.question_text];
        if (!fromEl) return null;

        const nameRect = fromEl.getBoundingClientRect();
        const fromX = (nameRect.left + nameRect.width / 2 - mainContentRect.left) / zoomLevel;
        let fromY;
        if (bottomNames.includes(q.question_text)) {
          fromY = (nameRect.top - mainContentRect.top - 5) / zoomLevel;
        } else {
          fromY = (nameRect.bottom - mainContentRect.top + 5) / zoomLevel;
        }
        const fromPos = { x: fromX, y: fromY };

        if (!q.accepted_answers || q.accepted_answers.length === 0) return null;
        const correctZoneName = q.accepted_answers[0];
        const zone = zones.find(z => z.name === correctZoneName);
        if (!zone) return null;

        const zoneCenterX = (zone.rect.x1 + zone.rect.x2) / 2;
        const zoneCenterY = (zone.rect.y1 + zone.rect.y2) / 2;

        const layoutZoneX = zoneCenterX / fitParams.scaleX + fitParams.offsetX;
        const layoutZoneY = zoneCenterY / fitParams.scaleY + fitParams.offsetY;

        const toPos = { x: layoutZoneX + imageOffset.left, y: layoutZoneY + imageOffset.top };

        return { fromPos, toPos, from: q.question_text, toZone: zone };
      }).filter(Boolean);

      setHintLines(newHintLines);
    } else {
      setHintLines([]);
    }
  }, [showHint, isReviewMode, isImageLoaded, refsReady, exerciseData, zoomLevel, layoutVersion]);

  // Handle active rendering of lines (continuous sync with layout)
  const [renderedLines, setRenderedLines] = useState([]);
  const [renderedHintLines, setRenderedHintLines] = useState([]);

  useLayoutEffect(() => {
    if (!refsReady || !isImageLoaded || !mainContentRef.current || !imageContainerRef.current || !exerciseData || !exerciseData.content_data) return;

    const mainRect = mainContentRef.current.getBoundingClientRect();
    const imgEl = imageContainerRef.current.querySelector('img');
    if (!imgEl) return;
    const imageRect = imgEl.getBoundingClientRect();
    const currentImageOffset = {
      top: (imageRect.top - mainRect.top) / zoomLevel,
      left: (imageRect.left - mainRect.left) / zoomLevel,
    };

    const layoutImageWidth = imageRect.width / zoomLevel;
    const layoutImageHeight = imageRect.height / zoomLevel;
    if (layoutImageWidth <= 0 || layoutImageHeight <= 0 || imgEl.naturalWidth <= 0) return;

    const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, imgEl.naturalWidth, imgEl.naturalHeight);
    if (!fitParams) return;

    const { zones } = exerciseData.content_data;

    const recalculateLine = (line) => {
      const fromEl = nameRefs.current[line.from];
      if (!fromEl) return line;

      const nameRect = fromEl.getBoundingClientRect();
      // If the element hasn't been completely painted or is invisible, nameRect.width might be 0
      if (nameRect.width === 0) return line;

      const fromX = (nameRect.left + nameRect.width / 2 - mainRect.left) / zoomLevel;
      let fromY;
      const middleIndex = Math.ceil(wordBankSafe.length / 2);
      const bottomNames = wordBankSafe.slice(middleIndex);

      if (bottomNames.includes(line.from)) {
        fromY = (nameRect.top - mainRect.top - 5) / zoomLevel;
      } else {
        fromY = (nameRect.bottom - mainRect.top + 5) / zoomLevel;
      }

      const fromPos = { x: fromX, y: fromY };

      let toPos = line.toPos;
      if (line.toZone) {
        const zone = zones.find(z => z.name === line.toZone.name);
        if (zone) {
          const zoneCenterX = (zone.rect.x1 + zone.rect.x2) / 2;
          const zoneCenterY = (zone.rect.y1 + zone.rect.y2) / 2;
          const layoutZoneX = zoneCenterX / fitParams.scaleX + fitParams.offsetX;
          const layoutZoneY = zoneCenterY / fitParams.scaleY + fitParams.offsetY;
          toPos = { x: layoutZoneX + currentImageOffset.left, y: layoutZoneY + currentImageOffset.top };
        }
      }
      return { ...line, fromPos, toPos };
    };

    const newRenderedLines = lines.map(recalculateLine);
    const newRenderedHintLines = hintLines.map(recalculateLine);

    setRenderedLines(newRenderedLines);
    setRenderedHintLines(newRenderedHintLines);
  }, [lines, hintLines, layoutVersion, refsReady, isImageLoaded, zoomLevel, exerciseData, wordBankSafe]);

  // Mouse Move
  useEffect(() => {
    const contentArea = mainContentRef.current;
    const handleMouseMove = (event) => {
      if (!selectedName || !contentArea) return;
      const mainContentRect = contentArea.getBoundingClientRect();
      const x = (event.clientX - mainContentRect.left) / zoomLevel;
      const y = (event.clientY - mainContentRect.top) / zoomLevel;
      setPreviewLine(prev => prev ? ({ ...prev, toPos: { x, y } }) : null);
    };

    if (selectedName && contentArea) {
      contentArea.addEventListener('mousemove', handleMouseMove);
    }
    return () => {
      if (contentArea) {
        contentArea.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [selectedName, zoomLevel]);

  // Style B — Sunset Adventure palette (Draw Lines design)
  const BNAME = {
    ink: '#2A1F3D', yellow: '#FFD23F', cream: '#FFF4E6',
    coral: '#FF5C5C', coralDk: '#B83838', plum: '#5B2E5E', aqua: '#3DC9B5',
  };

  const renderName = (name) => {
    const isSelected = selectedName === name;
    const isUsed = lines.some(line => line.from === name);
    const isExampleName = exampleLine?.from === name;
    const inactive = isUsed || isExampleName;

    let bg = BNAME.yellow;
    let shadow = '0 2px 6px rgba(139,111,71,0.08)';
    let color = BNAME.ink;
    let opacity = 1;
    let textDecoration = 'none';
    let cursor = 'grab';
    let transform = 'none';

    if (isSelected) {
      bg = BNAME.coral;
      shadow = '0 2px 6px rgba(139,111,71,0.08)';
      color = '#fff';
      transform = 'scale(1.05)';
    } else if (inactive) {
      bg = BNAME.cream;
      shadow = 'none';
      opacity = 0.5;
      textDecoration = 'line-through';
      cursor = 'default';
    }

    const namePill = (
      <div
        ref={el => { nameRefs.current[name] = el; }}
        onClick={() => handleNameClick(name)}
        style={{
          background: bg,
          color,
          opacity,
          padding: '6px 18px',
          borderRadius: 99,
          border: `1px solid #E8E0CC`,
          boxShadow: shadow,
          fontFamily: '"Nunito", "Baloo 2", sans-serif',
          fontSize: 14, fontWeight: 800,
          textDecoration,
          cursor,
          userSelect: 'none',
          transition: 'all 0.15s',
          transform,
        }}
      >
        {name.replace(/\(Example\)/gi, '').trim()}
      </div>
    );

    if (!isReviewMode) return React.cloneElement(namePill, { key: name });

    const question = exerciseData?.questions?.find(q => q.question_text === name);
    return (
      <div key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {namePill}
        {question && <QuestionLearnButton question={question} size={20} />}
      </div>
    );
  };

  // --- RENDER ---

  if (!exerciseData || !exerciseData.content_data) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    zones,
  } = exerciseData.content_data;

  const word_bank = exerciseData.questions.map(q => q.question_text);
  const middleIndex = Math.ceil(word_bank.length / 2);
  // Ensure SVG line updates when window layout changes happen but React might not.
  // The layoutVersion state trigger already takes care of most cases.

  const topNames = word_bank.slice(0, middleIndex);
  const bottomNames = word_bank.slice(middleIndex);

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 h-full p-2 md:p-4" style={{ fontFamily: '"Nunito", "Baloo 2", sans-serif' }}>
      <div
        className="relative flex flex-col items-center gap-3 flex-1 min-h-0"
        ref={mainContentRef}
        style={{
          background: '#fff',
          border: `3.5px solid ${BNAME.ink}`,
          boxShadow: '0 2px 6px rgba(139,111,71,0.08)',
          borderRadius: 24,
          padding: '18px 20px',
        }}
      >
        <div className="flex flex-wrap justify-around gap-2 w-full px-2 shrink-0">
          {topNames.map(name => renderName(name))}
        </div>

        <div
          className="relative cursor-crosshair w-full flex-1 min-h-0"
          ref={imageContainerRef}
          onClick={handleImageClick}
          style={{
            borderRadius: 18,
            border: `3px solid ${BNAME.ink}`,
            background: '#fff',
            overflow: 'hidden',
          }}
        >
          <img src={`${ASSET_BASE_URL}${image_url}`} alt="exercise illustration" onLoad={handleImageLoad} className="block w-full h-full object-contain mx-auto" />
        </div>

        <div className="flex flex-wrap justify-around gap-2 w-full px-2 shrink-0">
          {bottomNames.map(name => renderName(name))}
        </div>

        <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" style={{ minHeight: '100%' }}>
          {/* Zone group - translated to match image position + object-fit offset */}
          <g>
            {zones.map(zone => {
              let localScaleX = 1;
              let localScaleY = 1;
              let localOffsetX = 0;
              let localOffsetY = 0;

              if (imageContainerRef.current) {
                const img = imageContainerRef.current.querySelector('img');
                if (img && img.naturalWidth > 0) {
                  const ir = img.getBoundingClientRect();
                  const layoutImageWidth = ir.width / zoomLevel;
                  const layoutImageHeight = ir.height / zoomLevel;

                  const fitParams = getObjectFitSize(layoutImageWidth, layoutImageHeight, img.naturalWidth, img.naturalHeight);
                  if (fitParams) {
                    localScaleX = fitParams.scaleX;
                    localScaleY = fitParams.scaleY;
                    localOffsetX = fitParams.offsetX;
                    localOffsetY = fitParams.offsetY;
                  }
                }
              }

              if (localScaleX <= 0 || localScaleY <= 0) return null;

              return (
                <g key={zone.name} transform={`translate(${imageOffset.left + localOffsetX}, ${imageOffset.top + localOffsetY})`}>
                  <rect
                    x={zone.rect.x1 / localScaleX}
                    y={zone.rect.y1 / localScaleY}
                    width={(zone.rect.x2 - zone.rect.x1) / localScaleX}
                    height={(zone.rect.y2 - zone.rect.y1) / localScaleY}
                    fill="none"
                    stroke={BNAME.ink}
                    strokeWidth="2"
                    strokeDasharray="5 4"
                    rx="4"
                    opacity="0.55"
                  />
                </g>
              )
            })}
          </g>

          {renderedLines.map((line, index) => {
            if (!line.fromPos || !line.toPos || isNaN(line.fromPos.x) || isNaN(line.fromPos.y) || isNaN(line.toPos.x) || isNaN(line.toPos.y)) return null;

            let strokeColor = BNAME.coral;
            if (isReviewMode) {
              const question = exerciseData.questions.find(q => q.question_text === line.from);
              if (question) {
                if (question.is_correct === true || question.is_correct === 'true') {
                  strokeColor = BNAME.aqua;
                } else {
                  strokeColor = BNAME.coral;
                }
              }
            }

            return (
              <g key={index}>
                <line
                  x1={line.fromPos.x}
                  y1={line.fromPos.y}
                  x2={line.toPos.x}
                  y2={line.toPos.y}
                  stroke={strokeColor}
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
                <circle cx={line.fromPos.x} cy={line.fromPos.y} r="4.5" fill={strokeColor} stroke={BNAME.ink} strokeWidth="1.5" />
                <circle cx={line.toPos.x} cy={line.toPos.y} r="4.5" fill={strokeColor} stroke={BNAME.ink} strokeWidth="1.5" />
                {isReviewMode && (
                  <g transform={`translate(${(line.fromPos.x + line.toPos.x) / 2}, ${(line.fromPos.y + line.toPos.y) / 2})`}>
                    <circle r="10" fill="#fff" stroke={BNAME.ink} strokeWidth="2" />
                    <text textAnchor="middle" dy="4" fill={strokeColor} fontSize="13" fontWeight="800">
                      {strokeColor === BNAME.aqua ? "✓" : "✗"}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {previewLine && previewLine.fromPos && previewLine.toPos && !isNaN(previewLine.fromPos.x) && (
            <line
              x1={previewLine.fromPos.x}
              y1={previewLine.fromPos.y}
              x2={previewLine.toPos.x}
              y2={previewLine.toPos.y}
              stroke={BNAME.ink}
              strokeWidth="2.5"
              strokeDasharray="6 5"
              strokeLinecap="round"
              opacity="0.5"
            />
          )}

          {exampleLine && exampleLine.fromPos && exampleLine.toPos && (
            <g key="example-line">
              <line
                x1={exampleLine.fromPos.x}
                y1={exampleLine.fromPos.y}
                x2={exampleLine.toPos.x}
                y2={exampleLine.toPos.y}
                stroke={BNAME.ink}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <circle cx={exampleLine.fromPos.x} cy={exampleLine.fromPos.y} r="4" fill={BNAME.yellow} stroke={BNAME.ink} strokeWidth="1.5" />
              <circle cx={exampleLine.toPos.x} cy={exampleLine.toPos.y} r="4" fill={BNAME.yellow} stroke={BNAME.ink} strokeWidth="1.5" />
            </g>
          )}

          {(showHint || isReviewMode) && renderedHintLines.map((line, index) => {
            if (!line.fromPos || !line.toPos || isNaN(line.fromPos.x) || isNaN(line.toPos.x)) return null;
            const question = exerciseData.questions.find(q => q.question_text === line.from);
            // Fix: is_correct is at top level
            if (isReviewMode && question?.is_correct) return null;

            return (
              <g key={`hint-${index}`}>
                <line
                  x1={line.fromPos.x}
                  y1={line.fromPos.y}
                  x2={line.toPos.x}
                  y2={line.toPos.y}
                  stroke={BNAME.yellow}
                  strokeWidth="3"
                  strokeDasharray="6 5"
                  strokeLinecap="round"
                />
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  );
};

export default ListenPart1;